// components/AdminQuickEditModal.js
import React, { useState, useEffect, useContext } from 'react';
import {
  View,
  Text,
  Modal,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Alert
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { AuthContext } from '../contexts/AuthContext';
import { SheetContext } from '../contexts/SheetContext';
import EditableScoreCell from './EditableScoreCell';
import sheetService from '../services/sheetService';
import hapticService from '../services/hapticService';
import alertService from '../services/alertService';

const AdminQuickEditModal = ({ 
  visible, 
  onClose, 
  competitionName,
  riderName,
  riderNumber,
  laps,
  sections,
  availableSections = [], // 🆕 AJOUT UNIQUE
  initialScores = {}
}) => {
  const { getValidAccessToken, isUserAdmin } = useContext(AuthContext);
  const { sheetId } = useContext(SheetContext);
  
  const [scores, setScores] = useState({});
  const [originalScores, setOriginalScores] = useState({});
  const [modifiedCells, setModifiedCells] = useState(new Set());
  const [syncingCells, setSyncingCells] = useState(new Set());
  const [modificationCount, setModificationCount] = useState(0);
  const [lastModification, setLastModification] = useState(null);

  // Initialiser les scores au chargement
  useEffect(() => {
    if (visible) {
      setScores({ ...initialScores });
      setOriginalScores({ ...initialScores });
      setModifiedCells(new Set());
      setSyncingCells(new Set());
      setModificationCount(0);
      setLastModification(null);
    }
  }, [visible, initialScores]);

  // Vérifier les permissions admin
  if (!isUserAdmin()) {
    return null;
  }

  const handleCellValueChange = async (lapSection, newValue) => {
    const currentValue = scores[lapSection];
    
    if (currentValue === newValue) {
      return; // Pas de changement
    }

    // Sauvegarder pour annulation
    setLastModification({
      lapSection,
      oldValue: currentValue,
      newValue: newValue,
      timestamp: Date.now()
    });

    // Marquer comme modifié
    const newModifiedCells = new Set(modifiedCells);
    newModifiedCells.add(lapSection);
    setModifiedCells(newModifiedCells);
    
    // Incrémenter le compteur
    setModificationCount(prev => prev + 1);

    // Mettre à jour immédiatement l'UI
    setScores(prev => ({
      ...prev,
      [lapSection]: newValue
    }));

    // Marquer comme en cours de sync
    const newSyncingCells = new Set(syncingCells);
    newSyncingCells.add(lapSection);
    setSyncingCells(newSyncingCells);

    try {
      // Syncer avec le serveur
      await sheetService.updateScore(
        sheetId,
        competitionName,
        riderName,
        lapSection,
        newValue,
        getValidAccessToken
      );
      
      hapticService.success();
      
    } catch (error) {
      console.error('Error syncing score:', error);
      
      // Revenir à l'ancienne valeur en cas d'erreur
      setScores(prev => ({
        ...prev,
        [lapSection]: currentValue
      }));
      
      newModifiedCells.delete(lapSection);
      setModifiedCells(newModifiedCells);
      
      hapticService.error();
      alertService.showAlert('Sync Error', 'Failed to save changes. Please try again.');
      
    } finally {
      // 🔧 CORRECTION: Retirer du statut sync
      setSyncingCells(prev => {
        const updated = new Set(prev);
        updated.delete(lapSection);
        return updated;
      });
    }
  };

  const handleUndo = () => {
    if (!lastModification) return;
    
    const { lapSection, oldValue } = lastModification;
    
    handleCellValueChange(lapSection, oldValue);
    setLastModification(null);
    
    hapticService.button();
  };

  const calculateTotal = () => {
    let total = 0;
    Object.values(scores).forEach(score => {
      if (score && score !== '-' && !isNaN(parseInt(score))) {
        total += parseInt(score);
      }
    });
    return total;
  };

  const calculateLapTotal = (lap) => {
    let total = 0;
    // 🔧 CORRECTION: Utiliser availableSections ou fallback
    const sectionsToUse = availableSections.length > 0 ? availableSections : Array.from({length: sections}, (_, i) => i + 1);
    
    sectionsToUse.forEach(section => {
      const lapSection = `LAP${lap}_S${section}`;
      const score = scores[lapSection];
      if (score && score !== '-' && !isNaN(parseInt(score))) {
        total += parseInt(score);
      }
    });
    return total;
  };

  const handleClose = () => {
    if (modificationCount > 0) {
      alertService.showAlert(
        'Unsaved Changes',
        `You have ${modificationCount} unsaved change${modificationCount > 1 ? 's' : ''}. Changes are automatically saved.\n\nClose anyway?`,
        [
          { 
            text: 'Stay', 
            style: 'cancel',
            onPress: () => hapticService.button()
          },
          { 
            text: 'Close', 
            onPress: () => {
              hapticService.button();
              onClose();
            }
          }
        ]
      );
    } else {
      hapticService.button();
      onClose();
    }
  };

  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={handleClose}
    >
      <View style={styles.container}>
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            <View style={styles.adminBadge}>
              <Ionicons name="shield-checkmark" size={16} color="#fff" />
              <Text style={styles.adminBadgeText}>ADMIN</Text>
            </View>
            <Text style={styles.title}>Quick Edit</Text>
          </View>
          <TouchableOpacity onPress={handleClose} style={styles.closeButton}>
            <Ionicons name="close" size={24} color="#333" />
          </TouchableOpacity>
        </View>

        {/* Rider Info */}
        <View style={styles.riderInfo}>
          <View style={styles.riderHeader}>
            <View style={styles.numberCircle}>
              <Text style={styles.riderNumber}>{riderNumber}</Text>
            </View>
            <View style={styles.riderDetails}>
              <Text style={styles.riderName}>{riderName}</Text>
              <Text style={styles.competitionName}>{competitionName}</Text>
            </View>
          </View>
          
          <View style={styles.statsContainer}>
            <View style={styles.statItem}>
              <Text style={styles.statLabel}>Total Score</Text>
              <Text style={styles.statValue}>{calculateTotal()}</Text>
            </View>
            <View style={styles.statsRight}>
              <View style={styles.statItem}>
                <Text style={styles.statLabel}>Modified</Text>
                <Text style={[styles.statValue, styles.modifiedCount]}>
                  {modificationCount}
                </Text>
              </View>
              <TouchableOpacity
                style={[styles.undoButton, !lastModification && styles.undoButtonDisabled]}
                onPress={handleUndo}
                disabled={!lastModification}
              >
                <Ionicons name="arrow-undo" size={16} color={lastModification ? '#fff' : '#999'} />
                <Text style={[styles.undoButtonText, !lastModification && styles.undoButtonTextDisabled]}>
                  Undo
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>

        {/* Grid */}
        <ScrollView 
          style={styles.gridContainer}
          showsVerticalScrollIndicator={true}
        >
          <View style={styles.grid}>
            {/* Header Row */}
            <View style={styles.headerRow}>
              <View style={[styles.lapLabelCell, styles.headerCell]}>
                <Text style={styles.headerText}>Lap</Text>
              </View>
              {/* 🔧 CORRECTION: Utiliser availableSections */}
              {(availableSections.length > 0 ? availableSections : Array.from({ length: sections }, (_, i) => i + 1)).map(section => (
                <View key={`section-header-${section}`} style={[styles.sectionCell, styles.headerCell]}>
                  <Text style={styles.headerText}>S{section}</Text>
                </View>
              ))}
              <View style={[styles.totalLabelCell, styles.headerCell]}>
                <Text style={styles.headerText}>Total</Text>
              </View>
            </View>

            {/* Lap Rows */}
            {Array.from({ length: laps }, (_, lapIndex) => {
              const lapNumber = lapIndex + 1;
              return (
                <View key={`lap-${lapNumber}`} style={[styles.dataRow, lapIndex > 0 && styles.dataRowSpaced]}>
                  {/* Lap Label */}
                  <View style={[styles.lapLabelCell, styles.lapCell]}>
                    <Text style={styles.lapLabel}>L{lapNumber}</Text>
                  </View>
                  
                  {/* Score Cells */}
                  {/* 🔧 CORRECTION: Utiliser availableSections */}
                  {(availableSections.length > 0 ? availableSections : Array.from({ length: sections }, (_, i) => i + 1)).map(section => {
                    const lapSection = `LAP${lapNumber}_S${section}`;
                    return (
                      <View key={`${lapNumber}-${section}`} style={styles.sectionCell}>
                        <View style={styles.editableCellContainer}>
                          <EditableScoreCell
                            value={scores[lapSection] || ''}
                            onValueChange={(newValue) => handleCellValueChange(lapSection, newValue)}
                            isOriginal={originalScores[lapSection] && originalScores[lapSection] !== '-'}  // ✅ BONNE PROP
                            isModified={modifiedCells.has(lapSection)}
                            isSyncing={syncingCells.has(lapSection)}
                          />
                        </View>
                      </View>
                    );
                  })}
                  
                  {/* Total Cell */}
                  <View style={[styles.totalLabelCell, styles.totalCellContainer]}>
                    <Text style={styles.totalText}>{calculateLapTotal(lapNumber)}</Text>
                  </View>
                </View>
              );
            })}
          </View>
        </ScrollView>

        {/* Legend */}
        <View style={styles.legend}>
          <View style={styles.legendItem}>
            <View style={[styles.legendColor, styles.legendEmpty]} />
            <Text style={styles.legendText}>Empty</Text>
          </View>
          <View style={styles.legendItem}>
            <View style={[styles.legendColor, styles.legendOriginal]} />
            <Text style={styles.legendText}>Original</Text>
          </View>
          <View style={styles.legendItem}>
            <View style={[styles.legendColor, styles.legendModified]} />
            <Text style={styles.legendText}>Modified</Text>
          </View>
          <View style={styles.legendItem}>
            <Ionicons name="sync" size={12} color="#0066cc" />
            <Text style={styles.legendText}>Syncing</Text>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  adminBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#e91e63',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
    marginRight: 12,
  },
  adminBadgeText: {
    color: '#fff',
    fontSize: 12,
    
    fontFamily: 'Montserrat_700Bold',
    marginLeft: 4,
  },
  title: {
    fontSize: 20,
    
    color: '#333',
    fontFamily: 'Montserrat_700Bold',
  },
  closeButton: {
    padding: 8,
  },
  riderInfo: {
    backgroundColor: '#fff',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  riderHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  numberCircle: {
    width: 60,
    height: 60,
    borderRadius: 30,
    backgroundColor: '#0066cc',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  riderNumber: {
    color: '#fff',
    
    fontSize: 20,
    fontFamily: 'Montserrat_700Bold',
  },
  riderDetails: {
    flex: 1,
  },
  riderName: {
    fontSize: 22,
    
    color: '#333',
    fontFamily: 'Montserrat_700Bold',
  },
  competitionName: {
    fontSize: 20,
    color: '#666',
    fontFamily: 'Montserrat_400Regular',
  },
  statsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  statItem: {
    alignItems: 'center',
  },
  statsRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  statLabel: {
    fontSize: 14,
    color: '#666',
    fontFamily: 'Montserrat_400Regular',
  },
  statValue: {
    fontSize: 22,
    
    color: '#333',
    fontFamily: 'Montserrat_700Bold',
  },
  modifiedCount: {
    color: '#ff9800',
  },
  undoButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#ff9800',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
  },
  undoButtonDisabled: {
    backgroundColor: '#e0e0e0',
  },
  undoButtonText: {
    color: '#fff',
    fontSize: 12,
    
    fontFamily: 'Montserrat_600SemiBold',
    marginLeft: 4,
  },
  undoButtonTextDisabled: {
    color: '#999',
  },
  gridContainer: {
    flex: 1,
    backgroundColor: '#fff',
  },
  grid: {
    paddingHorizontal: 4,
    paddingVertical: 16,
  },
  headerRow: {
    flexDirection: 'row',
    marginBottom: 8,
    paddingHorizontal: 2,
  },
  dataRow: {
    flexDirection: 'row',
    marginBottom: 2,
    paddingHorizontal: 2,
  },
  dataRowSpaced: {
    marginTop: 8,
  },
  
  // Largeurs optimisées pour écran complet
  lapLabelCell: {
    width: 45,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 3,
  },
  sectionCell: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    marginHorizontal: 1,
    minWidth: 40,
  },
  totalLabelCell: {
    width: 55,
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: 3,
  },
  
  // Conteneur pour cellules éditables
  editableCellContainer: {
    width: '100%',
    height: 45,
    justifyContent: 'center',
    alignItems: 'center',
  },
  
  // Styles des cellules d'en-tête
  headerCell: {
    height: 35,
    backgroundColor: '#0066cc',
    borderRadius: 4,
    justifyContent: 'center',
    alignItems: 'center',
  },
  
  // Styles des cellules de données
  lapCell: {
    height: 45,
    backgroundColor: '#0066cc',
    borderRadius: 4,
    justifyContent: 'center',
    alignItems: 'center',
  },
  totalCellContainer: {
    height: 45,
    backgroundColor: '#0066cc',
    borderRadius: 4,
    justifyContent: 'center',
    alignItems: 'center',
  },
  
  // Textes
  headerText: {
    color: '#fff',
    fontSize: 15,
    
    fontFamily: 'Montserrat_700Bold',
  },
  lapLabel: {
    fontSize: 15,
    
    color: '#fff',
    fontFamily: 'Montserrat_700Bold',
  },
  totalText: {
    fontSize: 18,
    
    color: '#fff',
    fontFamily: 'Montserrat_700Bold',
  },
  
  // Légende
  legend: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    alignItems: 'center',
    padding: 12,
    backgroundColor: '#fff',
    borderTopWidth: 1,
    borderTopColor: '#e0e0e0',
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  legendColor: {
    width: 12,
    height: 12,
    borderRadius: 2,
    marginRight: 4,
    borderWidth: 1,
    borderColor: '#ddd',
  },
  legendEmpty: {
    backgroundColor: '#f8f8f8',
  },
  legendOriginal: {
    backgroundColor: '#ddeeff',
  },
  legendModified: {
    backgroundColor: '#fff3e0',
  },
  legendText: {
    fontSize: 11,
    color: '#666',
    fontFamily: 'Montserrat_400Regular',
  },
});

export default AdminQuickEditModal;